import React, { useEffect, useMemo, useRef, useState } from 'react';
import { placeLabel } from '../lib/placeLabel';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Users, Luggage, Plane, User, Send, Plus, X, Zap, Briefcase, BellOff } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import PhoneInput from '../components/ui/phone-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AdminLayout from '../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAPS_LIBRARIES = ['places'];
const AS_DIRECTED_HOURS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 12];

// Match the public website exactly
const SERVICE_TYPES = [
  { id: 'Transfer', name: 'Transfer (Point-to-Point / Airport)' },
  { id: 'As Directed Service', name: 'As Directed Service (Hourly)' },
];

const emptyStop = () => ({ address: '', clientName: '', clientPhone: '', notes: '' });

const Dispatch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || null;
  const [vehicles, setVehicles] = useState([]);
  useEffect(() => {
    axios.get(`${API}/fleet`)
      .then((res) => {
        const list = res.data.vehicles || [];
        setVehicles(list);
        setForm((prev) => (prev.vehicle ? prev : { ...prev, vehicle: list[0]?.id || '' }));
      })
      .catch(() => setVehicles([]));
  }, []);
  const { isLoaded: gmapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GMAPS_LIBRARIES,
  });

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const [form, setForm] = useState({
    jobType: 'customer',
    notifyCustomer: true,
    subcontractClient: '',
    subcontractReference: '',
    serviceType: 'Transfer',
    vehicle: '',
    pickupLocation: '',
    dropoffLocation: '',
    stops: [],
    hours: '4',
    pickupDate: today,
    pickupTime: '09:00',
    passengers: '1',
    luggage: '1',
    flightNumber: '',
    returnRequired: 'no',
    returnDate: '',
    returnTime: '',
    firstName: prefill?.firstName || '',
    lastName: prefill?.lastName || '',
    email: prefill?.email || '',
    phone: prefill?.phone || '',
    specialRequests: '',
    quoteAmount: '',
    sendEmail: true,
    hideCustomerPhone: false,
  });

  const [pickupAC, setPickupAC] = useState(null);
  const [dropoffAC, setDropoffAC] = useState(null);
  const [stopACs, setStopACs] = useState({});
  const [distance, setDistance] = useState('');
  const [travelTime, setTravelTime] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitMode, setSubmitMode] = useState(null); // 'quote' | 'confirm' — drives button-level spinner

  // Passenger autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const isAsDirected = form.serviceType === 'As Directed Service';
  const isSubcontract = form.jobType === 'subcontract';

  const updateField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Job type helpers — subcontract forces notifications off
  const setJobType = (jt) =>
    setForm((prev) => ({
      ...prev,
      jobType: jt,
      notifyCustomer: jt === 'subcontract' ? false : true,
      sendEmail: jt === 'subcontract' ? false : prev.sendEmail,
    }));

  // Stop helpers — each stop carries an address + optional client details
  const addStop = () => setForm((p) => ({ ...p, stops: [...p.stops, emptyStop()] }));
  const removeStop = (idx) => setForm((p) => ({ ...p, stops: p.stops.filter((_, i) => i !== idx) }));
  const updateStop = (idx, key, value) =>
    setForm((p) => ({
      ...p,
      stops: p.stops.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    }));
  const onStopPlace = (idx) => {
    const ac = stopACs[idx];
    if (ac) {
      const p = ac.getPlace();
      updateStop(idx, 'address', placeLabel(p));
    }
  };

  const validStops = form.stops.filter((s) => (s.address || '').trim());

  // Distance / time calculation with waypoints support
  useEffect(() => {
    if (isAsDirected || !gmapsLoaded || !window.google?.maps) return;
    if (!form.pickupLocation || !form.dropoffLocation) {
      setDistance('');
      setTravelTime('');
      return;
    }
    setCalculating(true);
    const stops = form.stops.filter((s) => (s.address || '').trim());

    const formatResult = (totalMeters, totalSeconds) => {
      setDistance((totalMeters / 1609.34).toFixed(1));
      const mins = totalSeconds / 60;
      const h = Math.floor(mins / 60);
      const m = Math.round(mins % 60);
      setTravelTime(
        h > 0
          ? `${h} hour${h > 1 ? 's' : ''} ${m} minute${m !== 1 ? 's' : ''}`
          : `${m} minute${m !== 1 ? 's' : ''}`
      );
    };

    if (stops.length > 0) {
      const ds = new window.google.maps.DirectionsService();
      ds.route(
        {
          origin: form.pickupLocation,
          destination: form.dropoffLocation,
          waypoints: stops.map((s) => ({ location: s.address, stopover: true })),
          travelMode: 'DRIVING',
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        },
        (resp, status) => {
          if (status === 'OK' && resp?.routes?.[0]) {
            let m = 0;
            let s = 0;
            resp.routes[0].legs.forEach((leg) => {
              m += leg.distance.value;
              s += leg.duration.value;
            });
            formatResult(m, s);
          }
          setCalculating(false);
        }
      );
    } else {
      const dm = new window.google.maps.DistanceMatrixService();
      dm.getDistanceMatrix(
        {
          origins: [form.pickupLocation],
          destinations: [form.dropoffLocation],
          travelMode: 'DRIVING',
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        },
        (resp, status) => {
          if (status === 'OK' && resp.rows[0].elements[0].status === 'OK') {
            const r = resp.rows[0].elements[0];
            formatResult(r.distance.value, r.duration.value);
          }
          setCalculating(false);
        }
      );
    }
  }, [form.pickupLocation, form.dropoffLocation, JSON.stringify(form.stops), gmapsLoaded, isAsDirected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced customer lookup for autocomplete
  useEffect(() => {
    const q = form.firstName.trim();
    if (q.length < 1) {
      setSuggestions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/customers`, { params: { q, limit: 8 } });
        setSuggestions(data.customers || []);
      } catch (e) {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [form.firstName]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pickCustomer = (c) => {
    setForm((prev) => ({
      ...prev,
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      email: c.email || '',
      phone: c.phone || '',
    }));
    setShowSuggestions(false);
  };

  const blockEnter = (e) => { if (e.key === 'Enter') e.preventDefault(); };

  const onPickupPlace = () => {
    if (pickupAC) {
      const p = pickupAC.getPlace();
      updateField('pickupLocation', placeLabel(p, form.pickupLocation));
    }
  };
  const onDropoffPlace = () => {
    if (dropoffAC) {
      const p = dropoffAC.getPlace();
      updateField('dropoffLocation', placeLabel(p, form.dropoffLocation));
    }
  };

  const handleSubmit = async (e, mode = 'confirm') => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.phone) {
      toast.error('Please fill in the passenger name and phone');
      return;
    }
    // Customer jobs that notify the customer require a valid email
    if (!isSubcontract && form.notifyCustomer && !form.email) {
      toast.error('An email is required to notify the customer');
      return;
    }
    if (!form.pickupLocation) {
      toast.error('Pickup location is required');
      return;
    }
    if (isAsDirected) {
      if (!form.hours) {
        toast.error('Please select number of hours');
        return;
      }
    } else if (!form.dropoffLocation) {
      toast.error('Drop-off location is required');
      return;
    }
    if (isSubcontract && !form.subcontractClient.trim()) {
      toast.error('Please enter the client firm for this subcontract job');
      return;
    }
    if (mode === 'quote' && (!form.quoteAmount || parseFloat(form.quoteAmount) <= 0)) {
      toast.error('Quote amount is required to send a quotation');
      return;
    }

    setSubmitting(true);
    setSubmitMode(mode);
    try {
      const vehicleName = vehicles.find((v) => v.id === form.vehicle)?.name || form.vehicle;
      const stops = form.stops
        .filter((s) => (s.address || '').trim())
        .map((s) => ({
          address: s.address.trim(),
          clientName: (s.clientName || '').trim(),
          clientPhone: (s.clientPhone || '').trim(),
          notes: (s.notes || '').trim(),
        }));

      const notifyCustomer = isSubcontract ? false : form.notifyCustomer;

      const payload = {
        jobType: form.jobType,
        notifyCustomer,
        subcontractClient: isSubcontract ? form.subcontractClient.trim() : '',
        subcontractReference: isSubcontract ? form.subcontractReference.trim() : '',
        hideCustomerPhone: form.hideCustomerPhone,
        serviceType: form.serviceType,
        vehicle: form.vehicle,
        vehicleName,
        pickupLocation: form.pickupLocation,
        dropoffLocation: isAsDirected
          ? `As Directed - ${form.hours} hours (${parseInt(form.hours, 10) * 15} miles allowance)`
          : form.dropoffLocation,
        stops,
        stopovers: stops.map((s) => s.address),
        pickupDate: form.pickupDate,
        pickupTime: form.pickupTime,
        flightNumber: form.flightNumber,
        returnRequired: !isAsDirected ? form.returnRequired : 'no',
        returnDate: !isAsDirected && form.returnRequired === 'yes' ? form.returnDate : '',
        returnTime: !isAsDirected && form.returnRequired === 'yes' ? form.returnTime : '',
        passengers: form.passengers,
        luggage: form.luggage,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        specialRequests: form.specialRequests,
        hours: isAsDirected ? form.hours : '',
        calculatedDistance: !isAsDirected && distance ? String(distance) : '',
        calculatedTime: !isAsDirected && travelTime ? String(travelTime) : '',
      };
      if (form.quoteAmount && parseFloat(form.quoteAmount) > 0) {
        payload.quoteAmount = parseFloat(form.quoteAmount);
      }

      const sendEmail = isSubcontract ? false : form.sendEmail;
      const { data } = await axios.post(`${API}/admin/dispatch/bookings`, payload, {
        params: { send_email: sendEmail, mode },
      });
      toast.success(
        mode === 'quote'
          ? `Quotation ${data.id} sent to customer`
          : isSubcontract
            ? `Subcontract job ${data.id} created (driver-only)`
            : `Booking ${data.id} created`,
      );
      navigate('/admin/bookings');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSubmitting(false);
      setSubmitMode(null);
    }
  };

  const stopsSectionLabel = isAsDirected ? 'Stops (client pickups)' : 'Stopovers';

  const renderStops = () => (
    <div className="space-y-3">
      {form.stops.map((s, idx) => (
        <div key={idx} className="rounded-lg border border-gray-200 p-3 space-y-3 bg-gray-50/50" data-testid={`dispatch-stop-card-${idx}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {isAsDirected ? `Stop ${idx + 1}` : `Stopover ${idx + 1}`}
            </span>
            <button
              type="button"
              onClick={() => removeStop(idx)}
              className="text-red-600 hover:text-red-700 text-xs flex items-center gap-1"
              data-testid={`dispatch-remove-stop-${idx}`}
            >
              <X className="h-3 w-3" /> Remove
            </button>
          </div>
          <div>
            <Label className="mb-1.5 text-xs font-medium text-gray-700">Address</Label>
            {gmapsLoaded ? (
              <Autocomplete
                onLoad={(ac) => setStopACs((prev) => ({ ...prev, [idx]: ac }))}
                onPlaceChanged={() => onStopPlace(idx)}
                options={{ componentRestrictions: { country: 'gb' } }}
              >
                <Input
                  value={s.address}
                  onChange={(e) => updateStop(idx, 'address', e.target.value)}
                  className="bg-white"
                  placeholder="Start typing an address…"
                  onKeyDown={blockEnter}
                  data-testid={`dispatch-stop-address-${idx}`}
                />
              </Autocomplete>
            ) : (
              <Input value={s.address} onChange={(e) => updateStop(idx, 'address', e.target.value)} className="bg-white" />
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 text-xs font-medium text-gray-700">Client name (optional)</Label>
              <Input
                value={s.clientName}
                onChange={(e) => updateStop(idx, 'clientName', e.target.value)}
                className="bg-white"
                placeholder="Who is picked up here?"
                data-testid={`dispatch-stop-client-name-${idx}`}
              />
            </div>
            <div>
              <Label className="mb-1.5 text-xs font-medium text-gray-700">Client phone (optional)</Label>
              <PhoneInput
                value={s.clientPhone}
                onChange={(val) => updateStop(idx, 'clientPhone', val)}
                className="bg-white"
                data-testid={`dispatch-stop-client-phone-${idx}`}
              />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 text-xs font-medium text-gray-700">Notes (optional)</Label>
            <Input
              value={s.notes}
              onChange={(e) => updateStop(idx, 'notes', e.target.value)}
              className="bg-white"
              placeholder="e.g. wait 10 mins, call on arrival"
              data-testid={`dispatch-stop-notes-${idx}`}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addStop}
        className="text-sm text-gray-700 hover:text-black flex items-center gap-1.5 font-medium"
        data-testid="dispatch-add-stop-btn"
      >
        <Plus className="h-4 w-4" /> Add {isAsDirected ? 'Stop' : 'Stopover'}
      </button>
    </div>
  );

  return (
    <AdminLayout title="Dispatch" subtitle="Create a confirmed booking directly (phone, walk-in & subcontract)">
      {prefill && (
        <div className="mb-4 max-w-4xl bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="dispatch-prefill-banner">
          <User className="h-4 w-4" />
          <p className="text-sm">
            Pre-filled for <span className="font-semibold">{prefill.firstName} {prefill.lastName}</span>
            {prefill.email && <span className="text-emerald-700"> · {prefill.email}</span>}
          </p>
        </div>
      )}
      <Card className="p-6 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Type */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
              <Briefcase className="h-4 w-4" /> Job Type
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setJobType('customer')}
                className={`text-left rounded-lg border p-4 transition-colors ${!isSubcontract ? 'border-black bg-black text-white' : 'border-gray-200 bg-white hover:border-gray-400'}`}
                data-testid="dispatch-jobtype-customer"
              >
                <p className="text-sm font-semibold">Standard (customer)</p>
                <p className={`text-xs mt-1 ${!isSubcontract ? 'text-gray-300' : 'text-gray-500'}`}>Books a passenger and sends them confirmation/quote emails.</p>
              </button>
              <button
                type="button"
                onClick={() => setJobType('subcontract')}
                className={`text-left rounded-lg border p-4 transition-colors ${isSubcontract ? 'border-black bg-black text-white' : 'border-gray-200 bg-white hover:border-gray-400'}`}
                data-testid="dispatch-jobtype-subcontract"
              >
                <p className="text-sm font-semibold">Subcontract / Driver-only</p>
                <p className={`text-xs mt-1 ${isSubcontract ? 'text-gray-300' : 'text-gray-500'}`}>Work for another firm. Driver is notified, the passenger is never emailed.</p>
              </button>
            </div>

            {isSubcontract && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="dispatch-subcontract-fields">
                <div>
                  <Label className="mb-2 font-medium text-gray-900">Client firm</Label>
                  <Input
                    value={form.subcontractClient}
                    onChange={(e) => updateField('subcontractClient', e.target.value)}
                    className="bg-white"
                    placeholder="e.g. Prestige Cars Ltd"
                    data-testid="dispatch-subcontract-client"
                  />
                </div>
                <div>
                  <Label className="mb-2 font-medium text-gray-900">Their reference (optional)</Label>
                  <Input
                    value={form.subcontractReference}
                    onChange={(e) => updateField('subcontractReference', e.target.value)}
                    className="bg-white"
                    placeholder="Job / booking ref"
                    data-testid="dispatch-subcontract-reference"
                  />
                </div>
              </div>
            )}

            {isSubcontract && (
              <div className="flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="dispatch-subcontract-notice">
                <BellOff className="h-4 w-4 shrink-0" />
                Customer notifications are disabled for subcontract jobs. Only the assigned driver will be notified.
              </div>
            )}
          </div>

          {/* Service + Vehicle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 font-medium text-gray-900">Service Type</Label>
              <Select value={form.serviceType} onValueChange={(v) => updateField('serviceType', v)}>
                <SelectTrigger className="bg-white" data-testid="dispatch-service-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 font-medium text-gray-900">Vehicle</Label>
              <Select value={form.vehicle} onValueChange={(v) => updateField('vehicle', v)}>
                <SelectTrigger className="bg-white" data-testid="dispatch-vehicle-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1.5">{v.name}{v.isElectric && <Zap className="h-3.5 w-3.5 text-emerald-600" />}</span>
                        <span className="text-[11px] text-gray-500">{[v.category, `${v.passengers} PAX`, `${v.luggage} luggage`].filter(Boolean).join(' · ')}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Passenger with autocomplete */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
              <User className="h-4 w-4" /> {isSubcontract ? 'Passenger (from client firm)' : 'Passenger'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" ref={wrapperRef}>
              <div className="relative">
                <Label className="mb-2 font-medium text-gray-900">First Name</Label>
                <Input
                  value={form.firstName}
                  onChange={(e) => { updateField('firstName', e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Start typing to search existing customers…"
                  className="bg-white"
                  data-testid="dispatch-first-name"
                  autoComplete="off"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div
                    className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-auto"
                    data-testid="dispatch-customer-suggestions"
                  >
                    {suggestions.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => pickCustomer(c)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        data-testid={`dispatch-suggestion-${c.id}`}
                      >
                        <p className="text-sm font-medium text-black truncate">
                          {c.firstName} {c.lastName}
                          {c.phone && <span className="text-gray-500 font-normal"> · {c.phone}</span>}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{c.email} · {c.totalBookings} job{c.totalBookings === 1 ? '' : 's'}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-2 font-medium text-gray-900">Last Name</Label>
                <Input
                  value={form.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  className="bg-white"
                  data-testid="dispatch-last-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 font-medium text-gray-900">Email {isSubcontract && <span className="text-gray-400 font-normal">(optional)</span>}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className="bg-white"
                  data-testid="dispatch-email"
                />
              </div>
              <div>
                <Label className="mb-2 font-medium text-gray-900">Phone</Label>
                <PhoneInput
                  value={form.phone}
                  onChange={(val) => updateField('phone', val)}
                  className="bg-white"
                  data-testid="dispatch-phone"
                />
              </div>
            </div>
          </div>

          {/* Journey */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Journey
            </h3>

            <div>
              <Label className="mb-2 font-medium text-gray-900">Pickup Location</Label>
              {gmapsLoaded ? (
                <Autocomplete onLoad={setPickupAC} onPlaceChanged={onPickupPlace} options={{ componentRestrictions: { country: 'gb' } }}>
                  <Input
                    value={form.pickupLocation}
                    onChange={(e) => updateField('pickupLocation', e.target.value)}
                    className="bg-white"
                    placeholder="Start typing an address…"
                    onKeyDown={blockEnter}
                  data-testid="dispatch-pickup-location"
                  />
                </Autocomplete>
              ) : (
                <Input value={form.pickupLocation} onChange={(e) => updateField('pickupLocation', e.target.value)} className="bg-white" />
              )}
            </div>

            {!isAsDirected && (
              <>
                {/* Stopovers with per-stop client details */}
                {renderStops()}

                <div>
                  <Label className="mb-2 font-medium text-gray-900">Drop-off Location</Label>
                  {gmapsLoaded ? (
                    <Autocomplete onLoad={setDropoffAC} onPlaceChanged={onDropoffPlace} options={{ componentRestrictions: { country: 'gb' } }}>
                      <Input
                        value={form.dropoffLocation}
                        onChange={(e) => updateField('dropoffLocation', e.target.value)}
                        className="bg-white"
                        placeholder="Start typing an address…"
                        onKeyDown={blockEnter}
                  data-testid="dispatch-dropoff-location"
                      />
                    </Autocomplete>
                  ) : (
                    <Input value={form.dropoffLocation} onChange={(e) => updateField('dropoffLocation', e.target.value)} className="bg-white" />
                  )}
                </div>

                {(distance || travelTime || calculating) && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-4 rounded-r-lg" data-testid="dispatch-distance-time-card">
                    {calculating ? (
                      <p className="text-sm text-gray-700">Calculating distance and travel time…</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {distance && (
                          <div>
                            <p className="text-xs text-gray-600 mb-1">
                              Distance{validStops.length ? ' (via stops)' : ''}
                            </p>
                            <p className="text-lg font-bold text-gray-900" data-testid="dispatch-distance-value">{distance} miles</p>
                          </div>
                        )}
                        {travelTime && (
                          <div>
                            <p className="text-xs text-gray-600 mb-1">Est. Travel Time</p>
                            <p className="text-lg font-bold text-gray-900" data-testid="dispatch-time-value">{travelTime}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {isAsDirected && (
              <>
                <div>
                  <Label className="mb-2 font-medium text-gray-900">Hours (15 miles/hour allowance)</Label>
                  <Select value={form.hours} onValueChange={(v) => updateField('hours', v)}>
                    <SelectTrigger className="bg-white" data-testid="dispatch-hours-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {AS_DIRECTED_HOURS.map((h) => (
                        <SelectItem key={h} value={String(h)}>{h} hours ({h * 15} miles)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* As-Directed multi-stop client pickups */}
                <div>
                  <Label className="mb-2 font-medium text-gray-900">{stopsSectionLabel}</Label>
                  {renderStops()}
                </div>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Pickup Date</Label>
                <Input type="date" value={form.pickupDate} min={today} onChange={(e) => updateField('pickupDate', e.target.value)} className="bg-white" data-testid="dispatch-pickup-date" />
              </div>
              <div>
                <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Pickup Time</Label>
                <Input type="time" value={form.pickupTime} onChange={(e) => updateField('pickupTime', e.target.value)} className="bg-white" data-testid="dispatch-pickup-time" />
              </div>
            </div>

            {!isAsDirected && (
              <div className="border-t pt-4">
                <Label className="mb-2 font-medium text-gray-900">Return Journey?</Label>
                <div className="flex gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => updateField('returnRequired', 'no')}
                    className={`flex-1 py-2.5 rounded-md border-2 text-sm font-medium transition-all ${form.returnRequired === 'no' ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-700 hover:border-black'}`}
                    data-testid="dispatch-return-no"
                  >
                    No
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField('returnRequired', 'yes')}
                    className={`flex-1 py-2.5 rounded-md border-2 text-sm font-medium transition-all ${form.returnRequired === 'yes' ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-700 hover:border-black'}`}
                    data-testid="dispatch-return-yes"
                  >
                    Yes
                  </button>
                </div>
                {form.returnRequired === 'yes' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Return Date</Label>
                        <Input type="date" value={form.returnDate} min={form.pickupDate} onChange={(e) => updateField('returnDate', e.target.value)} className="bg-white" data-testid="dispatch-return-date" />
                      </div>
                      <div>
                        <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Return Time</Label>
                        <Input type="time" value={form.returnTime} onChange={(e) => updateField('returnTime', e.target.value)} className="bg-white" data-testid="dispatch-return-time" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">On confirmation this creates a second, separately-assignable return job (drop-off &rarr; pickup).</p>
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Passengers</Label>
                <Input type="number" min="1" value={form.passengers} onChange={(e) => updateField('passengers', e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Luggage className="h-3.5 w-3.5" /> Luggage</Label>
                <Input type="number" min="0" value={form.luggage} onChange={(e) => updateField('luggage', e.target.value)} className="bg-white" />
              </div>
              <div>
                <Label className="mb-2 font-medium text-gray-900 flex items-center gap-1.5"><Plane className="h-3.5 w-3.5" /> Flight Number</Label>
                <Input value={form.flightNumber} onChange={(e) => updateField('flightNumber', e.target.value)} placeholder="(optional)" className="bg-white" />
              </div>
            </div>

            <div>
              <Label className="mb-2 font-medium text-gray-900">Special Requests</Label>
              <Textarea value={form.specialRequests} onChange={(e) => updateField('specialRequests', e.target.value)} rows={3} className="bg-white resize-none" />
            </div>
          </div>

          {/* Pricing + email toggle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <Label className="mb-2 font-medium text-gray-900">Quote Amount (£)</Label>
              <Input type="number" step="0.01" min="0" value={form.quoteAmount} onChange={(e) => updateField('quoteAmount', e.target.value)} placeholder="Required for ‘Send Quotation’" className="bg-white" data-testid="dispatch-quote-amount" />
            </div>
            <div className="flex items-end">
              {isSubcontract ? (
                <p className="text-sm text-gray-500 flex items-center gap-2" data-testid="dispatch-subcontract-noemail">
                  <BellOff className="h-4 w-4" /> No customer email will be sent
                </p>
              ) : (
                <label className="flex items-center gap-2 text-sm text-gray-700 select-none" data-testid="dispatch-send-email-toggle">
                  <input
                    type="checkbox"
                    checked={form.sendEmail}
                    onChange={(e) => { updateField('sendEmail', e.target.checked); updateField('notifyCustomer', e.target.checked); }}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Send confirmation email to customer
                </label>
              )}
            </div>
          </div>

          {/* Chauffeur privacy */}
          <div className="pt-2">
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none" data-testid="dispatch-hide-phone-toggle">
              <input
                type="checkbox"
                checked={form.hideCustomerPhone}
                onChange={(e) => updateField('hideCustomerPhone', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Hide customer phone number from the chauffeur
            </label>
          </div>

          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin')} className="border-gray-300">
              Cancel
            </Button>
            {!isSubcontract && (
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, 'quote')}
                disabled={submitting}
                variant="outline"
                className="border-black text-black hover:bg-gray-100"
                data-testid="dispatch-send-quote-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting && submitMode === 'quote' ? 'Sending…' : 'Send Quotation'}
              </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="bg-black text-white hover:bg-gray-800"
              data-testid="dispatch-submit-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting && submitMode === 'confirm'
                ? 'Creating…'
                : isSubcontract ? 'Create Subcontract Job' : 'Create Booking'}
            </Button>
          </div>
        </form>
      </Card>
    </AdminLayout>
  );
};

export default Dispatch;
